import React from 'react';
import {
  X, Upload, Image as ImageIcon, Crop as CropIcon, RotateCcw, RotateCw,
  FlipHorizontal, FlipVertical, Clipboard, Check
} from 'lucide-react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

/**
 * The parts of an image upload modal that every flow renders the same way.
 *
 * Kept as small standalone pieces rather than one configurable shell component: the flows differ
 * mainly in their MIDDLE editing steps, so each modal composes these and renders its own steps in
 * between. Every part takes `u` — the object returned by useImageUpload.
 */

/** Modal skeleton: header, error banner, body, footer. */
export const ShellFrame = ({ title, onClose, error, footer, children }) => (
  <div className="stui-modal-overlay">
    <div className="stui-modal stui-modal-lg stui-imgupload-modal">
      <div className="stui-modal-header">
        <h3><ImageIcon size={18} className="stui-imgupload-title-icon" />{title}</h3>
        <button className="stui-modal-close" onClick={onClose}><X size={20} /></button>
      </div>
      <div className="stui-modal-body stui-imgupload-body">
        {error && <div className="stui-imgupload-error">{error}</div>}
        {children}
      </div>
      <div className="stui-modal-footer">{footer}</div>
    </div>
  </div>
);

/** Drop zone, file picker and clipboard paste. `specText` states what the output will be. */
export const SelectStep = ({ u, maxSizeMB, allowedTypes, specText }) => (
  <div className="stui-imgupload-select">
    <div className={`stui-imgupload-dropzone${u.dragOver ? ' dragover' : ''}`}
      onClick={() => u.inputRef.current?.click()}
      onDragOver={e => { e.preventDefault(); u.setDragOver(true); }}
      onDragLeave={() => u.setDragOver(false)}
      onDrop={u.handleDrop}>
      <Upload size={40} className="stui-imgupload-drop-icon" />
      <p className="stui-imgupload-drop-title">Drop image here or click to browse</p>
      <p className="stui-imgupload-drop-sub">Max {maxSizeMB}MB • {allowedTypes.split(',').join(', ')}</p>
      <div className="stui-imgupload-spec">
        {specText}
        {u.outputSpec && <><br />Output: <strong>{u.outputSpec}</strong></>}
      </div>
    </div>
    <input ref={u.inputRef} type="file" accept={allowedTypes} className="stui-imgupload-file-input"
      onChange={e => { if (e.target.files[0]) u.handleFileSelect(e.target.files[0]); e.target.value = ''; }} />
    <div className="stui-imgupload-paste-row">
      <button className="stui-btn stui-btn-secondary stui-btn-sm" onClick={u.handlePasteButton}><Clipboard size={14} /> Paste from clipboard</button>
      <span className="stui-imgupload-paste-hint">or Ctrl+V</span>
    </div>
  </div>
);

/**
 * Crop stage. `aspectRatio` undefined means free-form — ReactCrop treats an undefined `aspect`
 * as unconstrained, which is exactly what photo uploads want.
 */
export const CropStep = ({ u, aspectRatio, hint }) => (
  <div className="stui-imgupload-crop">
    <div className="stui-imgupload-crop-banner">
      <CropIcon size={14} className="stui-imgupload-crop-banner-icon" />
      <span className="stui-imgupload-crop-banner-text">{hint}</span>
    </div>
    <div className="stui-imgupload-canvas-wrap">
      <ReactCrop crop={u.crop} onChange={c => u.setCrop(c)} aspect={aspectRatio} className="stui-imgupload-reactcrop">
        <img src={u.sourceDataUrl} alt="Crop source" onLoad={u.onCropImageLoad} className="stui-imgupload-crop-img" />
      </ReactCrop>
    </div>
  </div>
);

export const UploadingStep = () => (
  <div className="stui-imgupload-uploading">
    <Upload size={32} className="stui-spinning stui-imgupload-uploading-icon" />
    <span className="stui-imgupload-uploading-text">Uploading...</span>
  </div>
);

/**
 * Preview pane. `opaque` drops the transparency checkerboard — a checkerboard behind an opaque
 * format implies an alpha channel it cannot carry.
 * The pick props are only used by flows with a colour pipette; photo flows omit them.
 */
export const PreviewPane = ({ u, opaque, picking, pickHint, imgRef, onImageClick }) => (
  <div className={`stui-imgupload-preview-pane${picking ? ' picking' : ''}`}>
    <div className="stui-imgupload-preview-box">
      {u.previewUrl && (
        <img
          ref={imgRef}
          src={u.previewUrl}
          alt="Preview"
          onClick={onImageClick}
          className={`stui-imgupload-preview-img${opaque ? ' stui-imgupload-preview-img-opaque' : ''}`}
        />
      )}
    </div>
    {picking && pickHint && <div className="stui-imgupload-pick-hint">{pickHint}</div>}
  </div>
);

/** One numbered step in the right-hand workflow column. `done` swaps the number for a tick. */
export const ToolSection = ({ num, done, label, children }) => (
  <div className="stui-imgupload-section">
    <div className="stui-imgupload-step-label">
      <span className={`stui-imgupload-step-num${done ? ' done' : ''}`}>
        {done ? <Check size={10} /> : num}
      </span>
      {label}
    </div>
    {children}
  </div>
);

/** Step 1 in every flow: crop is already applied, offer a re-crop. */
export const RecropSection = ({ u, onRecrop }) => (
  <ToolSection num={1} done label="Crop">
    <button className="stui-imgupload-tool-btn" onClick={() => {
      u.setCroppedDataUrl(null);
      u.setCrop(undefined);
      onRecrop?.();
      u.setStep('crop');
    }}>
      <CropIcon size={12} /> Re-crop
    </button>
  </ToolSection>
);

/** Last step in every flow: rotate and flip. */
export const OrientationSection = ({ u, num }) => {
  const touched = u.rotation !== 0 || u.flipH || u.flipV;
  return (
    <ToolSection num={num} done={touched} label="Orientation">
      <div className="stui-imgupload-btn-group">
        <button className="stui-imgupload-tool-btn" onClick={() => u.setRotation((u.rotation + 270) % 360)} title="Rotate left 90°"><RotateCcw size={14} /></button>
        <button className="stui-imgupload-tool-btn" onClick={() => u.setRotation((u.rotation + 90) % 360)} title="Rotate right 90°"><RotateCw size={14} /></button>
        <button className={`stui-imgupload-tool-btn${u.flipH ? ' active' : ''}`} onClick={() => u.setFlipH(!u.flipH)} title="Flip horizontal"><FlipHorizontal size={14} /></button>
        <button className={`stui-imgupload-tool-btn${u.flipV ? ' active' : ''}`} onClick={() => u.setFlipV(!u.flipV)} title="Flip vertical"><FlipVertical size={14} /></button>
      </div>
      {u.rotation !== 0 && <div className="stui-imgupload-rotation-note">{u.rotation}°</div>}
    </ToolSection>
  );
};

/** File name field plus the resolved output dimensions/size/format. */
export const MetaSection = ({ u, maxSizeMB, formatLabel }) => (
  <div className="stui-imgupload-meta">
    <div className="stui-imgupload-filename">
      <div className="stui-imgupload-meta-label">File Name</div>
      <input className="stui-field-input stui-imgupload-filename-input" value={u.fileName}
        onChange={e => u.setFileName(e.target.value)} placeholder="filename" />
    </div>
    {u.outputInfo && (
      <div className="stui-imgupload-fileinfo">
        {u.outputInfo.width} x {u.outputInfo.height} &bull; {u.outputInfo.size} &bull; {formatLabel}
        {u.outputInfo.overLimit && <span className="stui-imgupload-overlimit"> (over {maxSizeMB}MB!)</span>}
      </div>
    )}
  </div>
);

/** Cancel, plus the primary action for the current step. */
export const ShellFooter = ({ u, onClose }) => (
  <>
    <button className="stui-btn stui-btn-secondary" onClick={onClose} disabled={u.uploading}>Cancel</button>
    {u.step === 'crop' && (
      <button className="stui-btn stui-btn-primary" onClick={u.handleCropApply} disabled={!u.hasCrop}>
        <CropIcon size={14} /> Apply Crop
      </button>
    )}
    {u.step === 'edit' && (
      <button className="stui-btn stui-btn-primary" onClick={u.handleUpload} disabled={u.uploading || u.outputInfo?.overLimit}>
        <Upload size={14} /> Upload
      </button>
    )}
  </>
);
